const url = require('url');
const { markdown } = require('markdown');
const PriceFormatter = require('./PriceFormatter');
const Env = require('./Env');
const textUtils = require('./utils/texts');

module.exports = (context = {}) => {
    const { format: formatPrice, formatWithForcedDecimals } = PriceFormatter(context);

    return {
        debug,
        renderImage,
        imageResizer: renderImage,
        asset,
        staticUrl,
        getStaticsBaseUrl,
        viewport,
        resize,
        isDesktop,
        isTablet,
        isMobile,
        random,
        dateFormat,
        markdown: renderMarkdown,
        formatPrice,
        formatWithForcedDecimals,
        icon: name => `<i class="nico-${name}"></i>`,
        ...textUtils
    };

    /**
     * Alias of console.log
     */
    function debug(msg) {
        console.log(msg);
    }

    /**
     * Returns the full URL of an asset
     * @param {string} file Asset filename (with extension)
     * @param {string} versionTimestamp (optional) timestamp to add to the url
     * @example staticUrl('alex-cruceros/index.css')
     * @returns {string} The asset's url
     */
    function staticUrl(file, versionTimestamp) {
        let trimmedFile = file.replace(/^\//, '');

        if (Env.isDevelopment(context.NODE_ENV)) {
            trimmedFile = trimmedFile.replace('alexandria', 'alexandria/development');
        }

        if (Env.isStaging(context.NODE_ENV)) {
            trimmedFile = trimmedFile.replace('alexandria', 'alexandria/staging');
        }

        const assetUrl = getStaticsBaseUrl() + trimmedFile;

        if (versionTimestamp) {
            return `${assetUrl}?${versionTimestamp}`;
        }
        return assetUrl;
    }

    /**
     * Returns the base url of assets
     * @param {string} assetsPath (optional) relative path where assets are located
     * @param {*} devHost (optional) port for development environments
     */
    function getStaticsBaseUrl(assetsPath = '', devHost = `localhost:8080`) {
        /* Local */
        if (Env.isLocal(context.NODE_ENV) || Env.isDevelopment(context.NODE_ENV)) {
            const { PORT = false } = process.env;

            return new url.URL(assetsPath, `${PORT ? `http://localhost:${PORT}` : `http://${devHost}`}`).href;
        }

        let cdnBase;
        if (context.mscdn.startsWith('http')) {
            cdnBase = context.mscdn;
        } else {
            cdnBase = `http://${context.mscdn}`;
        }
        const { href } = new url.URL(assetsPath, cdnBase);
        return href.replace(/https?:/, '');
    }

    /**
     * Generates image URLs to resize and crop images
     *
     * @param {string} src source of the image ( without CDN )
     * @param {object} [{
     *     operation, suboperation, width, height, quality = 80
     * }={}]    Options object:
     *              operation - first type of operation: resize
     *              suboperation - second type of operation: crop
     *              width - width you want for your image
     *              height - height you want for your image
     *
     * @returns {string} finalSource - return a final source of image resized and cropped
     * @example renderImage(Model.image)
     * @example renderImage(Model.image, "resize", 1000, 375)
     * @example renderImage(Model.image, "resize", "crop", 1000, 375)
     */
    function renderImage(src, { operation, suboperation, width, height, quality = 80 } = {}) {
        const regex = /.*((cdn|ssl)\.(logitravel|alitravel|traveltool|bookingay)\..*?)\/+(.*\.(jpg|png|gif))(\?.*)?/gim;

        let finalSource = prepareSrc();

        if (operation && width >= 0 && height >= 0) {
            if (finalSource.startsWith('//')) {
                finalSource = finalSource.substring(2);
            }

            if (suboperation && suboperation !== '') {
                finalSource = [
                    context.mscdn,
                    '/wsimgresize/',
                    operation,
                    '/',
                    suboperation,
                    '/',
                    width,
                    '/',
                    height,
                    '/',
                    finalSource
                ].join('');
            } else {
                finalSource = [
                    context.mscdn,
                    '/wsimgresize/',
                    operation,
                    '/',
                    width,
                    '/',
                    height,
                    '/',
                    finalSource
                ].join('');
            }
        }

        const querySeparator = finalSource.indexOf('?') === -1 ? '?' : '&';
        finalSource += `${querySeparator}jpegQuality=${quality}`;
        return finalSource;

        function prepareSrc() {
            const match = regex.exec(src);
            if (match !== null) {
                const relative = match[4];
                const params = match[6] || '';

                return [context.mscdn, '/', relative, params].join('');
            }
            if (src.startsWith('http') || src.startsWith('https') || src.startsWith('//')) {
                return src.replace(/(^\w+:|^)\/\//, '');
            }
            if (src.startsWith('/')) {
                return [context.mscdn, src].join('');
            }
            return [context.mscdn, '/', src].join('');
        }
    }

    /**
     * Construct the full url of images with added protocol
     * @param {string} path - source of the image ( without CDN )
     * @returns {string} return a final source of image with CDN added
     * @example assetImg('http://logitravel.com/photo.jpg')
     */
    function asset(path) {
        const image = renderImage(path);
        let protocol = '';
        if (path.startsWith('http:')) {
            protocol = 'http:';
        } else if (path.startsWith('https:')) {
            protocol = 'https:';
        }
        return protocol ? [protocol, image.startsWith('//') ? '' : '//', image].join('') : image;
    }

    /**
    * Construct the url of images to resize and crop depending on viewport's size

    * @param {string} src - source of the image ( without CDN )
    * @param {string} [operation] - first type of operation: resize
    * @param {string} [suboperation] - second type of operation: crop
    * @param {number} [width] - width you want for your image
    * @param {number} [height] - height you want for your image
    * @param {number} [viewportWidth] - width of the viewport
    * @param {number} [viewportHeight] - height of the viewport
    * @returns {string} return a final source of image using renderImage with a calculated size
    * @example viewport(Model.image, "resize", "crop", 1000, 375, 1980, 1080)
    */

    /**
     * Construct the url of images to resize and crop depending on viewport's size
     *
     * @param {any} src source of the image ( without CDN )
     * @param {any} [{
     *     operation, suboperation, width, height, viewportWidth, viewportHeight
     * }={}] Options:
     *          operation - first type of operation: resize
     *          suboperation - second type of operation: crop
     *          width - width you want for your image
     *          height - height you want for your image
     *          viewportWidth - width of the viewport
     *          viewportHeight - height of the viewport
     * @returns {string} return a final source of image using renderImage with a calculated size
     * @example viewport(Model.image, "resize", "crop", 1000, 375, 1980, 1080)
     */
    function viewport(src, { operation, suboperation, width, height, viewportWidth, viewportHeight } = {}) {
        const finalWidth = viewportWidth ? Math.min(width, viewportWidth) : width;
        const finalHeight = viewportHeight ? Math.min(height, viewportHeight) : height;
        const options = {
            operation,
            suboperation,
            width: finalWidth,
            height: finalHeight
        };
        return renderImage(src, options);
    }

    /**
     * Alias for renderImage with resize operation
     */
    function resize(src, width, height) {
        const options = {
            operation: 'resize',
            suboperation: undefined,
            width,
            height
        };
        return renderImage(src, options);
    }

    /**
     * Checks if device is desktop
     * @returns {boolean} return if it's true
     */
    function isDesktop() {
        return context.device === 'desktop';
    }

    /**
     * Checks if device is tablet
     * @returns {boolean} return if it's true
     */
    function isTablet() {
        return context.device === 'tablet';
    }

    /**
     * Checks if device is mobile
     * @returns {boolean} return if it's true
     */
    function isMobile() {
        return context.device === 'mobile';
    }

    /**
     * Get a random number, perfect for ID's
     * @returns {number} return a random number
     */
    function random() {
        const out = Math.floor(Math.random() * 9999 + 1);
        return `r${out}`;
    }

    /**
     * Returns a date format
     *
     * @param {string} device
     * @param {string} app
     * @returns {string}
     */
    function dateFormat(device, app) {
        let format = 'DD, d MM, yy';
        if (device === 'mobile') {
            switch (app) {
                case 'logitravelru':
                    format = 'd-mm-yy';
                    break;
                case 'logitravelil':
                    format = 'd-mm-yy';
                    break;
                case 'logitravelae':
                    format = 'd-mm-yy';
                    break;
                default:
                    format = 'd M yy';
                    break;
            }
        } else {
            switch (app) {
                case 'logitravelru':
                    format = 'd M yy';
                    break;
                case 'logitravelil':
                    format = 'd MM yy';
                    break;
                case 'logitravelae':
                    format = 'd MM yy';
                    break;
                default:
                    format = 'D, d MM, yy';
                    break;
            }
        }
        return format;
    }

    function renderMarkdown(str = '') {
        let out = markdown.toHTML(str);
        if (out.substring(0, 3) === '<p>') {
            out = out.slice(3, -4);
        }
        return out;
    }
};
